import React from 'react';
import { useSite } from '@/contexts/SiteContext';
import { useCatalogFunnel, FunnelData } from '@/hooks/useCatalog';
import { LinkedViewsStrip } from '@/components/catalog/LinkedViewsStrip';
import { FunnelLayer } from '@/components/catalog/FunnelLayer';
import { BoundaryBand } from '@/components/catalog/BoundaryBand';
import { StageRow } from '@/components/catalog/StageRow';
import { SegmentedBar, Segment } from '@/components/catalog/SegmentedBar';
import { StageAnnotation } from '@/components/catalog/StageAnnotation';
import { UnionNote } from '@/components/catalog/UnionNote';
import { FootnoteList } from '@/components/catalog/FootnoteList';
import { LanguageCoverage } from '@/components/catalog/LanguageCoverage';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { catalogColors } from '@/components/catalog/catalogColors';
import { sourceLabel } from '@/components/catalog/sourceLabels';
import { formatDistanceToNow } from 'date-fns';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) { return n.toLocaleString(); }

// ─── KPI card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  headline: string;
  label: string;
  sub?: string;
  color?: string;
}

function KpiCard({ headline, label, sub, color = catalogColors.accentTeal }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex flex-col gap-1">
          <div className="text-3xl font-bold tabular-nums" style={{ color }}>{headline}</div>
          <div className="text-sm font-semibold text-foreground">{label}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Diff strip ───────────────────────────────────────────────────────────────

// totalScale = catalogProducts — aligns bar width to the same scale as the Catalog products row
function DiffStrip({ summary, completedAt, totalScale }: { summary: Record<string, unknown>; completedAt: string; totalScale: number }) {
  // feed_import_runs.summary uses either short keys (new, updated) or long keys (new_products, updated_products)
  const get = (short: string, long: string) => Number(summary[short] ?? summary[long] ?? 0);
  const nNew         = get('new',         'new_products');
  const nUpdated     = get('updated',     'updated_products');
  const nReactivated = get('reactivated', 'reactivated_products');
  const nUnchanged   = get('unchanged',   'unchanged_products');
  const nUnavailable = get('unavailable', 'unavailable_products');

  const feedTotal = nNew + nUpdated + nReactivated + nUnchanged + nUnavailable;
  // Use catalogProducts as the scale so the bar aligns with the Catalog products row
  const barScale = totalScale > 0 ? totalScale : (feedTotal || 1);

  const segments: Segment[] = [
    { label: 'New',         count: nNew,         color: catalogColors.accentTeal, tooltip: `New: ${fmt(nNew)}` },
    { label: 'Updated',     count: nUpdated,     color: catalogColors.infoBlue,   tooltip: `Updated: ${fmt(nUpdated)}` },
    { label: 'Reactivated', count: nReactivated, color: catalogColors.amber,      tooltip: `Reactivated: ${fmt(nReactivated)}` },
    { label: 'Unchanged',   count: nUnchanged,   color: catalogColors.gray,       tooltip: `Unchanged: ${fmt(nUnchanged)}` },
    { label: 'Unavailable', count: nUnavailable, color: catalogColors.warn,       tooltip: `Unavailable: ${fmt(nUnavailable)}` },
  ].filter(s => s.count > 0);

  return (
    // Same flex layout as StageRow: w-48 label | flex-1 bar | w-32 count
    <div className="flex items-center gap-3 min-h-[32px]">
      <div className="w-48 shrink-0 text-right">
        <span className="text-sm font-medium text-foreground">Last import</span>
        <div className="text-[10px] text-muted-foreground">
          {formatDistanceToNow(new Date(completedAt), { addSuffix: true })}
        </div>
      </div>
      <div className="flex-1 h-6 rounded overflow-hidden border" style={{ backgroundColor: 'white', borderColor: catalogColors.muted }}>
        <SegmentedBar segments={segments} total={barScale} height={24} />
      </div>
      <div className="w-32 shrink-0 text-right space-y-0.5">
        <div className="text-base font-bold tabular-nums">{fmt(feedTotal)}</div>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {segments.map((s, i) => (
            <div key={i} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.label.slice(0, 3)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CatalogFunnel() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || selectedSite?._id;
  const { data, isLoading, error } = useCatalogFunnel(siteId);

  return (
    <div className="p-6 max-w-[1440px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Catalog Pipeline Funnel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            End-to-end visibility from feed import to live on site
          </p>
        </div>
        <LinkedViewsStrip />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Failed to load funnel data. {(error as Error).message}
        </div>
      )}

      {!siteId && !isLoading && (
        <div className="text-center py-16 text-muted-foreground">Select a site to view the catalog funnel.</div>
      )}

      {isLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">{[0,1,2].map(i => <Skeleton key={i} className="h-28 rounded-lg" />)}</div>
          <Skeleton className="h-80 rounded-xl" />
        </div>
      )}

      {data && (() => {
        const { funnel: f, diff_bar, languages } = data;

        // Defensive defaults — gracefully handle old backend shape missing new fields
        const catalogProducts    = f.catalog_products ?? f.pfi_universe ?? 0;
        const aeoCandidateTotal  = f.aeo_candidate_total ?? f.aeo_generated ?? 0;
        const feedOnly           = f.aeo_candidate_feed_only ?? f.in_active_feed ?? 0;
        const manifestOnly       = f.aeo_candidate_manifest_only ?? 0;
        const feedAndManifest    = f.aeo_candidate_feed_and_manifest ?? 0;
        const ng = f.not_generated_breakdown ?? { in_queue: 0, needs_attention: 0, awaiting_enrichment: 0, failed: 0, other: 0 };
        const na = f.not_approved_breakdown  ?? { in_review_queue: 0, rejected: 0, quality_flagged: 0, archived: 0, other: 0 };
        const livePctVsGenerated = f.live_pct_vs_generated ?? 0;

        // D15: KPI values (D25: use server-side target_language_count + sections_per_page)
        const tlc = f.target_language_count ?? 17;
        const spp = f.sections_per_page ?? 13;
        const scopeManaged = aeoCandidateTotal;
        const contentSections = f.approved * spp;
        const globalReach = f.approved * spp * tlc;
        const preservationCount = manifestOnly;

        // D11: union bar segments
        const unionSegments: Segment[] = [
          {
            label: sourceLabel('feed_only'),
            count: feedOnly + feedAndManifest,
            color: catalogColors.infoBlue,
            tooltip: `In active feed with AEO filters: ${fmt(feedOnly + feedAndManifest)}`,
          },
          {
            label: sourceLabel('manifest_only'),
            count: manifestOnly,
            color: catalogColors.accentTeal,
            tooltip: `In publisher manifest but not in current feed: ${fmt(manifestOnly)}`,
          },
        ].filter(s => s.count > 0);

        // D12: not-generated breakdown
        const ngTotal = aeoCandidateTotal - f.aeo_generated;
        const ngSegments: Segment[] = [
          { label: 'Not yet generated',   count: ng.in_queue,            color: catalogColors.infoBlue, tooltip: `Enriched but AEO generation not yet triggered: ${fmt(ng.in_queue)}` },
          { label: 'Needs attention',     count: (ng as any).needs_attention ?? 0, color: catalogColors.amber,    tooltip: `Needs attention: ${fmt((ng as any).needs_attention ?? 0)}` },
          { label: 'Awaiting enrichment', count: ng.awaiting_enrichment, color: catalogColors.amber,    tooltip: `Awaiting enrichment: ${fmt(ng.awaiting_enrichment)}` },
          { label: 'Failed',              count: ng.failed,              color: catalogColors.warn,     tooltip: `Generation failed: ${fmt(ng.failed)}` },
          { label: 'Other',               count: ng.other,               color: catalogColors.gray,     tooltip: `Other / unknown: ${fmt(ng.other)}` },
        ].filter(s => s.count > 0);

        // D13: not-approved breakdown
        const naTotal = f.aeo_generated - f.approved;
        const naSegments: Segment[] = [
          { label: 'In review queue',  count: na.in_review_queue,    color: catalogColors.infoBlue, tooltip: `Awaiting review: ${fmt(na.in_review_queue)}` },
          { label: 'Rejected',         count: na.rejected,           color: catalogColors.warn,     tooltip: `Rejected: ${fmt(na.rejected)}` },
          { label: 'Quality flagged',  count: na.quality_flagged,    color: catalogColors.amber,    tooltip: `Quality flagged: ${fmt(na.quality_flagged)}` },
          { label: 'Archived',         count: (na as any).archived ?? 0,          color: catalogColors.gray,     tooltip: `Archived: ${fmt((na as any).archived ?? 0)}` },
          { label: 'Other',            count: na.other,              color: catalogColors.gray,     tooltip: `Other: ${fmt(na.other)}` },
        ].filter(s => s.count > 0);

        const footnotes = [
          `"Catalog products" = the total number of products PageMind has ingested for this site.`,
          `"AEO candidates" = products eligible for AEO content generation: those currently active in the feed and passing the site's filters, plus any products with a live URL on the merchant site. Products in both groups are counted once.`,
          `"Enriched" = product data has been processed and structured by PageMind, making it ready for AEO content generation.`,
          `Each percentage shows progress vs the immediately prior stage — e.g. "% Live" shows how many generated products have a matched live URL on the merchant site.`,
          `Translation percentages are calculated against the number of AEO-generated products, so coverage is always ≤ 100%.`,
        ];

        return (
          <>
            {/* D15: KPI strip */}
            <div className={`grid gap-4 ${tlc > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <KpiCard
                headline={fmt(scopeManaged)}
                label="Products under active AEO management"
                color={catalogColors.accentTeal}
              />
              <KpiCard
                headline={fmt(contentSections)}
                label="AI-generated content sections live across your catalog"
                color={catalogColors.infoBlue}
              />
              {tlc > 0 && (
                <KpiCard
                  headline={globalReach >= 1_000_000 ? `${(globalReach / 1_000_000).toFixed(1)}M` : fmt(globalReach)}
                  label={`Translations delivered across ${tlc} languages`}
                  color={catalogColors.purple}
                />
              )}
            </div>

            {/* Layer 1: Catalog universe */}
            <FunnelLayer
              variant="informational"
              tag="Informational"
              title="Catalog universe"
              subtitle="Background context — percentages are of total catalog products"
            >
              <StageRow
                label="Catalog products"
                count={catalogProducts}
                priorCount={catalogProducts}
                color={catalogColors.gray}
                isBaseline
              />

              {diff_bar && (
                <DiffStrip summary={diff_bar.summary} completedAt={diff_bar.completed_at} totalScale={catalogProducts} />
              )}

              <StageRow
                label="Enriched (KBE)"
                subLabel="has knowledge base entry"
                count={f.enriched}
                priorCount={catalogProducts}
                color={catalogColors.infoBlue}
              />
            </FunnelLayer>

            <BoundaryBand text="The AEO working set begins. Gaps below are measured against the AEO candidate baseline, not the entire catalog." />

            {/* Layer 2: AEO working set */}
            <FunnelLayer
              variant="analytical"
              tag="Analytical"
              title="AEO working set"
              subtitle="Actionable pipeline — percentages are vs the immediately prior stage"
            >
              {/* canonical_mode badge — neutral info descriptor, not a warning */}
              {f.canonical_mode !== undefined && (
                <div className="flex items-center gap-1.5 text-xs mb-1">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: catalogColors.infoBlue + '18', color: catalogColors.infoBlue }}
                  >
                    {f.canonical_mode ? 'Scope: canonical products' : 'Scope: full catalog'}
                  </span>
                </div>
              )}
              {/* AEO candidates: additive bar */}
              <StageRow
                label="AEO candidates"
                subLabel="union of feed + manifest"
                count={aeoCandidateTotal}
                priorCount={catalogProducts}
                color={catalogColors.accentTeal}
              >
                <SegmentedBar segments={unionSegments} total={aeoCandidateTotal} height={24} />
              </StageRow>
              <UnionNote
                segments={[
                  { label: sourceLabel('feed_only'), count: feedOnly + feedAndManifest, color: catalogColors.infoBlue, description: 'active in feed AND custom_label_4 ∈ {1,2,4}' },
                  { label: sourceLabel('manifest_only'), count: manifestOnly, color: catalogColors.accentTeal, description: 'present in publisher manifest, not in current feed (sticky-inclusion)' },
                ]}
                total={aeoCandidateTotal}
              />

              {/* AEO generated */}
              <StageRow
                label="AEO generated"
                count={f.aeo_generated}
                priorCount={aeoCandidateTotal}
                color={catalogColors.accentTeal}
              />

              {/* Not generated breakdown */}
              {ngTotal > 0 && (
                <>
                  <StageRow
                    label="Not generated"
                    count={ngTotal}
                    priorCount={aeoCandidateTotal}
                    color={catalogColors.warn}
                  >
                    <SegmentedBar segments={ngSegments} total={ngTotal} height={24} />
                  </StageRow>
                  {ng.awaiting_enrichment > 0 && (
                    <StageAnnotation variant="warn">
                      {fmt(ng.awaiting_enrichment)} products are awaiting enrichment before AEO generation can run.
                    </StageAnnotation>
                  )}
                </>
              )}

              {/* Approved */}
              <StageRow
                label="Approved (EN source)"
                count={f.approved}
                priorCount={f.aeo_generated}
                color={catalogColors.accentDark}
              />

              {/* Not approved breakdown */}
              {naTotal > 0 && (
                <StageRow
                  label="Not approved"
                  count={naTotal}
                  priorCount={f.aeo_generated}
                  color={catalogColors.warn}
                >
                  <SegmentedBar segments={naSegments} total={naTotal} height={24} />
                </StageRow>
              )}

              {/* Live on merchant site (D14: vs generated) */}
              <StageRow
                label="Live on merchant site"
                count={f.live}
                priorCount={f.aeo_generated}
                color={catalogColors.purple}
              />

              {/* Translation coverage (parallel detail) */}
              {languages.length > 0 && (
                <div className="pt-3 border-t border-dashed" style={{ borderColor: catalogColors.accentTeal + '40' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded" style={{ backgroundColor: catalogColors.purple + '20', color: catalogColors.purple }}>
                      Parallel detail
                    </span>
                    <span className="text-xs text-muted-foreground">Per-language translation coverage vs approved EN source ({fmt(f.aeo_generated)})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {languages.map(({ language, completed, total }) => (
                      <LanguageCoverage
                        key={language}
                        language={language}
                        count={completed}
                        total={total ?? f.aeo_generated}
                      />
                    ))}
                  </div>
                </div>
              )}
            </FunnelLayer>

            <FootnoteList footnotes={footnotes} />
          </>
        );
      })()}
    </div>
  );
}
